import { useState, useEffect } from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const promptCategories = [
  {
    name: 'Content',
    items: [
      { name: 'Ask Anything', path: '/prompts/ask-anything' },
      { name: 'Blog Post', path: '/prompts/blog-post' },
      { name: 'Blog Post Outline', path: '/prompts/blog-post-outline' },
      { name: 'Article', path: '/prompts/article' },
      { name: 'Video Script', path: '/prompts/video-script' },
      { name: 'About Us Generator', path: '/prompts/about-us' }
    ]
  },
  {
    name: 'Social Media',
    items: [
      { name: 'Instagram Post', path: '/prompts/instagram-post' },
      { name: 'Instagram Reel', path: '/prompts/instagram-reel' },
      { name: 'X Thread', path: '/prompts/x-thread' },
      { name: 'LinkedIn Article', path: '/prompts/linkedin-article' }
    ]
  },
  {
    name: 'Marketing',
    items: [
      { name: 'Email Campaign', path: '/prompts/email-campaign' },
      { name: 'Product Description', path: '/prompts/product-description' },
      { name: 'Ad Copy', path: '/prompts/ad-copy' }
    ]
  }
];

function Home() {
  useEffect(() => {
    // Add JSON-LD structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      'name': 'Content Creator AI',
      'description': 'AI-powered content generation tool for creating blog posts, social media content, and marketing copy.',
      'applicationCategory': 'Content Creation Tool',
      'operatingSystem': 'Web Browser',
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'USD'
      },
      'featureList': [
        'Blog Post Generation',
        'Social Media Content Creation',
        'Marketing Copy Writing',
        'Article Generation',
        'Video Script Creation'
      ]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Content Creator AI - Create Amazing Content with AI</title>
        <meta name="description" content="Transform your content creation process with our AI-powered writing assistant. Generate blog posts, social media content, and marketing copy effortlessly." />
        <link rel="canonical" href="https://contentcreatorai.com" />
      </Helmet>

      <main className="max-w-7xl mx-auto px-10 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-white to-gray-50">
          <div className="text-center py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8 leading-tight">
              Creating Great Content Has Never Been Easier
            </h1>
            <div className="max-w-3xl mx-auto space-y-8">
              <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
                We've all been there – staring at a blank page, not knowing where to start. That's where our innovative AI writing assistant comes in. Its state-of-the-art algorithm helps you generate content quickly, effortlessly, and with creativity.
              </p>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  No Accounts. No Fees. Just Results.
                </h2>
                <p className="text-gray-700">
                  Now completely free, our tools are supported by ads and affiliate links so you can create without limits. By engaging with the ads on our site, you help us keep this powerful resource free for everyone.
                </p>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                <h2 className="text-2xl font-semibold text-blue-900 mb-4">
                  Support Us While You Create
                </h2>
                <p className="text-blue-800">
                  Love what we're offering? Clicking on an ad or exploring our affiliate links directly supports our mission to provide accessible, high-quality content creation tools at no cost to you.
                </p>
              </div>

              <div className="mt-8">
                <p className="text-xl font-medium text-gray-900">
                  Start writing today—no sign-ups, no strings attached!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-16 sm:py-24 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Explore Our Content Creation Tools
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {promptCategories.map((category) => (
                <div key={category.name} className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {category.name}
                  </h3>
                  <ul className="space-y-3">
                    {category.items.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.path}
                          className="text-gray-600 hover:text-primary hover:underline block py-1"
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default Home;
